import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";

const NAVY = "#1B2A4A";
const NAVY_DARK = "#12203A";
const GOLD = "#C9A44A";

export default function ClientLogin() {
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setError("");
    try {
      const { data } = await api.post("/client/login", { email, password });
      localStorage.setItem("client_token", data.token);
      nav("/client");
    } catch (err) { setError(formatApiError(err)); }
    setBusy(false);
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-cream" data-testid="client-login-page">
      {/* Left — branding panel */}
      <div
        className="hidden md:flex flex-col justify-between relative overflow-hidden text-cream p-12"
        style={{ backgroundImage: "url(/brand/client-bg.jpg)", backgroundSize: "cover", backgroundPosition: "center" }}
        data-testid="client-login-brand-panel"
      >
        {/* Gentle vignette — soft navy edges fading inward */}
        <div
          aria-hidden
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse at center, rgba(18,32,58,0.18) 0%, rgba(18,32,58,0.38) 60%, rgba(10,18,36,0.62) 100%)",
          }}
        />
        <div className="relative" data-testid="client-login-brand-top">
          <img
            src="/brand/logo.png"
            alt="Dr. Mariea's Publication and Research International Pvt. Ltd"
            data-testid="client-login-logo"
            className="w-40 h-auto select-none drop-shadow-lg"
            draggable={false}
          />
          <div
            className="font-display mt-5 leading-snug max-w-xs"
            style={{ fontSize: "1.15rem", color: "#F4E9CE", textShadow: "0 1px 8px rgba(10,18,36,0.55)" }}
            data-testid="client-login-company"
          >
            Dr. Mariea's Publication and Research International Pvt. Ltd
          </div>
        </div>
        <div className="relative">
          <div className="label-caps" style={{ color: "rgba(244,233,206,0.75)" }}>Client Portal</div>
          <h1
            className="font-display text-5xl leading-[0.98] font-medium mt-2"
            style={{ textShadow: "0 2px 14px rgba(10,18,36,0.6)" }}
            data-testid="client-login-headline"
          >
            Your research.<br />Beautifully<br />published.
          </h1>
          <p className="mt-6 max-w-sm" style={{ color: "rgba(244,233,206,0.85)", textShadow: "0 1px 8px rgba(10,18,36,0.55)" }}>
            Track your publication's journey — milestones, drafts for approval and updates from our editorial team, in one calm workspace.
          </p>
        </div>
        <div className="relative text-xs" style={{ color: "rgba(244,233,206,0.6)" }}>
          © {new Date().getFullYear()} Dr. Mariea's Publication and Research International Pvt. Ltd
        </div>
      </div>

      {/* Right — login form */}
      <div className="flex items-center justify-center px-8 py-12">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="client-login-form">
          {/* Mobile-only brand mark */}
          <div className="md:hidden mb-8 text-center">
            <img src="/brand/logo.png" alt="Dr. Mariea's" className="w-24 h-auto mx-auto" draggable={false} />
            <div className="font-display mt-3 text-sm leading-snug" style={{ color: NAVY }}>
              Dr. Mariea's Publication and Research International Pvt. Ltd
            </div>
          </div>

          <div className="mb-8">
            <div className="label-caps mb-2" style={{ color: GOLD }}>Client sign in</div>
            <h2 className="font-display text-4xl" style={{ color: NAVY }}>Welcome.</h2>
          </div>
          <label className="label-caps block mb-1" style={{ color: NAVY }}>Email</label>
          <Input
            data-testid="client-email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="rounded-lg h-11 mb-4 focus-visible:ring-0"
            style={{ borderColor: "rgba(27,42,74,0.25)" }}
          />
          <label className="label-caps block mb-1" style={{ color: NAVY }}>Password</label>
          <Input
            data-testid="client-password"
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="rounded-lg h-11 focus-visible:ring-0"
            style={{ borderColor: "rgba(27,42,74,0.25)" }}
          />
          {error && (
            <div data-testid="client-login-error" className="mt-4 border border-[#B02A00] text-[#B02A00] px-3 py-2 text-sm rounded-lg">{error}</div>
          )}
          <button
            data-testid="client-login-submit"
            disabled={busy}
            className="mt-6 w-full h-11 rounded-full font-semibold text-sm tracking-wide transition-all duration-200 disabled:opacity-60 hover:brightness-110 active:scale-[0.99]"
            style={{ background: `linear-gradient(180deg, ${NAVY}, ${NAVY_DARK})`, color: "#F4E9CE", boxShadow: `0 10px 24px -10px rgba(18,32,58,0.6), inset 0 1px 0 rgba(201,164,74,0.35)` }}
          >
            {busy ? "Signing in…" : "Sign in →"}
          </button>
          <div className="mt-6 text-xs text-center" style={{ color: "rgba(27,42,74,0.55)" }}>
            Not a client? <Link to="/login" className="underline underline-offset-2" style={{ color: NAVY }}>Team sign in</Link>
          </div>
        </form>
      </div>
    </div>
  );
}
